import {
  UNLEASH_FEATURE_NAME,
  ApiResolver,
  ConsentStatus
} from '@schibsted-nmp/advertising-shared';
import { isFeatureEnabled } from '@client/core/atoms/unleashFeatures.js';
import { $config } from '@client/core/atoms/config.js';
import { $consentAtom } from '@client/core/atoms/consent.js';

import type { Window } from './Window.js';

import type { Api } from './index.js';

export const { globalPropertyName, unsafePropertyName } =
  ApiResolver.getGlobalPropertyNames('adn');

export class Resolver extends ApiResolver<
  Api.Uninitialized,
  Api.Initialized,
  Api.Loaded,
  Window
> {
  /**
   * Provides synchronous access to a fully loaded global reference.
   *
   * *Warning: throws if it's not fully loaded!*
   * @see {@linkcode ApiResolver.loadedGlobalRef}.
   */
  get [unsafePropertyName]() {
    return this.loadedGlobalRef;
  }

  protected readonly globalPropertyName = globalPropertyName;

  protected override initialize(
    window: Window.AsUninitialized
  ): asserts window is Window.AsInitialized {
    const globalRef = (window[this.globalPropertyName] ??= {});
    globalRef.calls ??= [];
  }

  protected override isEnabled() {
    if (!isFeatureEnabled(UNLEASH_FEATURE_NAME.enableAdnuntius)) return false;

    const config = $config.get();
    if (!config?.adServer.adn) return false;

    // Adnuntius is only enabled for non-consented users:
    return $consentAtom.get() === ConsentStatus.Rejected;
  }

  protected override isInitialized(
    window: typeof globalThis.window
  ): window is Window.AsInitialized {
    return window[this.globalPropertyName]?.calls !== undefined;
  }

  protected override isLoaded(
    globalRef: Api.Initialized | Api.Loaded
  ): globalRef is Api.Loaded {
    return 'request' in globalRef && typeof globalRef.request === 'function';
  }

  protected override enqueue(
    globalRef: Api.Initialized | Api.Loaded,
    executor: () => void
  ) {
    globalRef.calls.push(executor);
  }
}
